import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Star, Loader2, Upload, MessageSquare } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EVENTS = ["Wedding", "Birthday", "Baby Shower", "Light Decoration", "Home Decoration", "Varmala Entry", "Other"];
const EMPTY = { name: "", event_type: "Wedding", rating: "5", message: "", featured: true };

export default function Reviews() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [replies, setReplies] = useState({});
  const [replyingId, setReplyingId] = useState(null);
  const fileRef = useRef();

  const load = () => api.get("/admin/testimonials").then(({ data }) => {
    setRows(data);
    setReplies(Object.fromEntries(data.map((r) => [r.id, r.reply || ""])));
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveReply = async (id) => {
    setReplyingId(id);
    try {
      await api.patch(`/admin/testimonials/${id}`, { reply: replies[id] || "" });
      toast.success(replies[id] ? "Reply posted" : "Reply cleared");
      load();
    } catch { toast.error("Failed"); }
    finally { setReplyingId(null); }
  };

  const create = async () => {
    if (!form.name || !form.message) return toast.error("Name and message required");
    setSaving(true);
    const fd = new FormData();
    fd.append("name", form.name); fd.append("event_type", form.event_type);
    fd.append("rating", form.rating); fd.append("message", form.message);
    fd.append("featured", form.featured);
    if (file) fd.append("photo", file);
    try { await api.post("/admin/testimonials", fd); toast.success("Review added"); setOpen(false); setForm(EMPTY); setFile(null); load(); }
    catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };
  const toggle = async (r) => { await api.patch(`/admin/testimonials/${r.id}`, { featured: !r.featured }); load(); };
  const remove = async (id) => { await api.delete(`/admin/testimonials/${id}`); toast.success("Removed"); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-zinc-900">Customer Reviews</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-review-btn" className="gold-gradient text-white font-semibold"><Plus size={16} className="mr-1" /> Add Review</Button></DialogTrigger>
          <DialogContent className="bg-card border-pink-200 text-zinc-900">
            <DialogHeader><DialogTitle className="font-display text-xl">Add Review</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Customer Name</Label><Input value={form.name} data-testid="review-name" onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white border-pink-200" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Event</Label>
                  <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                    <SelectTrigger className="mt-1 bg-white border-pink-200" data-testid="review-event"><SelectValue /></SelectTrigger>
                    <SelectContent>{EVENTS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Rating</Label>
                  <Select value={form.rating} onValueChange={(v) => setForm({ ...form, rating: v })}>
                    <SelectTrigger className="mt-1 bg-white border-pink-200" data-testid="review-rating"><SelectValue /></SelectTrigger>
                    <SelectContent>{[5, 4, 3, 2, 1].map((n) => <SelectItem key={n} value={String(n)}>{n} ★</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Message</Label><Textarea value={form.message} data-testid="review-message" onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 bg-white border-pink-200" rows={3} /></div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Show on homepage</span>
                <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="review-featured" />
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="review-photo" />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="border-pink-200 text-zinc-700"><Upload size={15} className="mr-2" /> {file ? file.name : "Customer photo (optional)"}</Button>
              </div>
              <Button onClick={create} disabled={saving} data-testid="save-review-btn" className="w-full gold-gradient text-white font-semibold rounded-full">{saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null} Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((r) => (
          <div key={r.id} className="bg-card border border-pink-200 rounded-xl p-5" data-testid={`admin-review-${r.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                {(r.photo_url || r.photo_path) && <img src={r.photo_url || fileUrl(r.photo_path)} alt="" className="h-10 w-10 rounded-full object-cover" />}
                <div><div className="text-zinc-900 font-semibold text-sm">{r.name}</div><div className="text-xs text-gold">{r.event_type}</div></div>
              </div>
              <button onClick={() => remove(r.id)} className="text-zinc-400 hover:text-red-500" data-testid={`del-review-${r.id}`}><Trash2 size={15} /></button>
            </div>
            <div className="flex gap-0.5 my-2">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={13} className={i < r.rating ? "text-gold fill-current" : "text-pink-200"} />)}</div>
            <p className="text-sm text-zinc-600 line-clamp-3">{r.message}</p>
            <label className="flex items-center justify-between mt-3 pt-3 border-t border-pink-100 text-sm text-zinc-700">
              Show on homepage
              <Switch checked={r.featured} onCheckedChange={() => toggle(r)} data-testid={`toggle-review-${r.id}`} />
            </label>
            <div className="mt-3 pt-3 border-t border-pink-100">
              <Label className="text-xs text-zinc-600">Public reply (shows under this review)</Label>
              <Textarea
                value={replies[r.id] ?? ""}
                data-testid={`reply-input-${r.id}`}
                onChange={(e) => setReplies({ ...replies, [r.id]: e.target.value })}
                placeholder="Thank you so much! It was a joy decorating your event…"
                className="mt-1 bg-white border-pink-200 text-sm" rows={2}
              />
              <Button
                size="sm" onClick={() => saveReply(r.id)} disabled={replyingId === r.id}
                data-testid={`save-reply-${r.id}`}
                className="mt-2 gold-gradient text-white font-semibold"
              >
                {replyingId === r.id ? <Loader2 className="animate-spin mr-1" size={14} /> : <MessageSquare size={14} className="mr-1" />}
                {r.reply ? "Update Reply" : "Post Reply"}
              </Button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="col-span-full py-16 text-center text-zinc-500">No reviews yet</div>}
      </div>
    </div>
  );
}
